import { take } from 'redux-saga-test-plan/matchers';
import { all, call, debounce, getContext, put, race, select, takeEvery, takeLatest } from 'redux-saga/effects';
import i18n from '../../../i18n';
import type {
    FinnovaCodeColumn,
    IEbankingVT,
    IFetchFinnovaCodesRequest,
    IFinnovaCode,
    IIstKarte,
    IPowerOfAttorney,
    IIstProduct,
    IKontodepot,
    IProduct,
} from '../../api/domain';
import { VertragsStatus } from '../../api/domain';
import type { ICustomerBase } from '../../api/domain/customer-base';
import type { IAddress, ICommunicationEntries, ICrmKundenTopf, IEBankingContract, IFetchFinnovaRequest, IKarte } from '../../api/domain/finnova';
import type { IAccount } from '../../api/domain/finnova/account';
import type { FinnovaCodeTable } from '../../api/domain/finnova/code-table';
import type { FinnovaApiService } from '../../api/services';
import type { Action } from '../utils';
import { NotificationType } from '../../enum';
import { DefaultProductCategory } from '../../enum/product-category';
import { ProductPermission } from '../../enum/product-permission';
import type { Tenant } from '../../enum/tenant';
import { getTranslationSelector } from '../../utils/i18n-helpers';
import { isCardArt, isCreditCard } from '../../utils/product-helpers';
import { applicationSelectors } from '../application';
import {
    addNotification,
    hideProgressIndicator,
    selectCustomerBaseForEServices,
    selectCustomerBaseForProducts,
    showProgressIndicator,
} from '../application/application-actions';
import { getTenant } from '../application/application-selectors';
import { customerBaseApiSelectors } from '../customer-base-api';
import { logError } from '../logging-api/logging-api-actions';
import { fetchProducts, fetchProductsFailure, fetchProductsSuccess } from '../settings-api/settings-api-actions';
import {
    fetchAddresses,
    fetchAddressesFailure,
    fetchAddressesSuccess,
    fetchCardProducts,
    fetchCardProductsFailure,
    fetchCardProductsSuccess,
    fetchCodes,
    fetchCodesFailure,
    fetchCodesSuccess,
    fetchCommunicationEntries,
    fetchCommunicationEntriesFailure,
    fetchCommunicationEntriesSuccess,
    fetchEBankingContracts,
    fetchEBankingContractsFailure,
    fetchEBankingContractsSuccess,
    fetchIstCards,
    fetchIstCardsFailure,
    fetchIstCardsSuccess,
    fetchIstProducts,
    fetchIstProductsFailure,
    fetchIstProductsSuccess,
    fetchKannProducts,
    fetchKannProductsFailure,
    fetchKannProductsSuccess,
    fetchKontodepots,
    fetchKontodepotsFailure,
    fetchKontodepotsSuccess,
    fetchPowerOfAttorney,
    fetchPowerOfAttorneyFailure,
    fetchPowerOfAttorneySuccess,
    fetchSollProducts,
    fetchSollProductsFailure,
    fetchSollProductsSuccess,
} from './finnova-api-actions';
import * as finnovaApiSelectors from './finnova-api-selectors';
import de from './translations/de.json';
import en from './translations/en.json';
import fr from './translations/fr.json';
import it from './translations/it.json';
import { pascalCaseWords } from '../../utils/string-utils';

function* addTranslations() {
    i18n.addResources('de', de);
    i18n.addResources('en', en);
    i18n.addResources('fr', fr);
    i18n.addResources('it', it);

    yield;
}

// TODO: this thing is super weird - get rid of it if possible. it is well tested so you have good chances of catching bugs when refactoring
const fetchProductsActionTaker =
    (type: string, categories: string[] = []) =>
    (action: Action<any>) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        action.type === type && (action.payload?.categories || []).join(',') === categories.join(',');

// TODO: this would be easier to understand if the differentiation between cards and non-card was done on the outside or there were two separate helpers
/**
 * This function consolidates the "Kann products" (products that can be bought by the signed-in customer)
 * that are fetched from finnova with the "Kundentöpfe" (also from finnova) and the product settings saved
 * in ti&m customization services and fetched via the /products endpoint.
 * Both the Kundentöpfe and the product settings serve as potential "filter" for Kann products so that
 * some products will not be available for and displayed to the customer under in some cases.
 *
 * @param kundenToepfe - array of Finnova Kundentöpfe
 * @param products - array of additional product settings. product.finnovaId will match kundenTopf.ktArt for corresponding Kundentopf
 * @param artCodes - array of art codes that acts as a filter for the Kundentöpfe (via kundenTopf.ktArt)
 * @returns the filtered list of Kann products that should be shown to the customer
 */
function* getProductsAvailableInCustomization(kundenToepfe: ICrmKundenTopf[], products: IProduct[], artCodes: number[]) {
    const productsClone = [...products];
    const tenant: Tenant = yield select(getTenant);

    return kundenToepfe
        .filter(
            (kt) =>
                artCodes.includes(kt.artCode) &&
                ((kt.ktArt === undefined || kt.ktArt === null) && typeof kt.cardArt === 'number' ? isCardArt(tenant, kt.cardArt) : true),
        )
        .reduce<IProduct[]>((acc, kundenTopf) => {
            const isCardCategory: boolean = kundenTopf.ktArt === undefined || kundenTopf.ktArt === null;

            const index = isCardCategory
                ? // eslint-disable-next-line eqeqeq
                  productsClone.findIndex((p) => p.finnovaId == kundenTopf.cardArt && p.category === DefaultProductCategory.CARDS)
                : // eslint-disable-next-line eqeqeq
                  productsClone.findIndex((p) => p.finnovaId == kundenTopf.ktArt && p.category !== DefaultProductCategory.CARDS);

            const product = index > -1 && productsClone.splice(index, 1).pop();

            if (product) {
                acc.push(product);
            }

            return acc;
        }, []);
}

function* handleFetchKannProducts({ payload: categories }: Action<string[]>) {
    const api: FinnovaApiService = yield getContext('finnovaApiService');

    try {
        yield put(fetchProducts(categories));
        const { success } = yield race({
            success: take(fetchProductsActionTaker(fetchProductsSuccess.type, categories)),
            failure: take(fetchProductsActionTaker(fetchProductsFailure.type, categories)),
        });
        if (!success) {
            throw new Error('Could not fetch products from customization');
        }

        const products: IProduct[] = success.payload.products;
        const isStrongAuthenticated: boolean = yield select(applicationSelectors.isStrongAuthenticated);
        if (!isStrongAuthenticated) {
            yield put(fetchKannProductsSuccess(products));
        }
        let selectedCustomerBase: ICustomerBase | undefined = yield select(applicationSelectors.getSelectedCustomerBaseForProducts);
        if (!selectedCustomerBase) {
            const { payload } = yield take(selectCustomerBaseForProducts);
            selectedCustomerBase = payload;
        }
        if (!selectedCustomerBase) {
            throw new Error('Cant fetch kann products without customer base');
        }

        const request: IFetchFinnovaRequest = {
            languageCode: i18n.language,
            vtlnr: selectedCustomerBase.vtlnr,
            kdlnr: selectedCustomerBase.externalId,
        };
        const kundenToepfe: ICrmKundenTopf[] = yield call([api, api.getCrmKundenToepfe], request);
        const kannProductsAvailableInCustomization: IProduct[] = yield getProductsAvailableInCustomization(kundenToepfe, products, [0, 2]);

        yield put(fetchKannProductsSuccess(kannProductsAvailableInCustomization));
    } catch (error: unknown) {
        yield put(logError(`could not fetch kann products: ${error}`));
        yield put(fetchKannProductsFailure());
    }
}

function* handleFetchIstProducts({ payload: categories }: Action<string[]>) {
    const api: FinnovaApiService = yield getContext('finnovaApiService');

    try {
        const isStrongAuthenticated: boolean = yield select(applicationSelectors.isStrongAuthenticated);
        if (!isStrongAuthenticated) {
            throw new Error('Cannot fetch IST products for weak or anonymous users');
        }
        let selectedCustomerBase: ICustomerBase | undefined = yield select(applicationSelectors.getSelectedCustomerBaseForProducts);
        if (!selectedCustomerBase) {
            const { payload } = yield take(selectCustomerBaseForProducts);
            selectedCustomerBase = payload;
        }
        if (!selectedCustomerBase) {
            throw new Error('Cant fetch ist products without customer base');
        }

        yield put(fetchProducts(categories));
        const { success } = yield race({
            success: take(fetchProductsActionTaker(fetchProductsSuccess.type, categories)),
            failure: take(fetchProductsActionTaker(fetchProductsFailure.type, categories)),
        });
        if (!success) {
            throw new Error('Could not fetch products from customization');
        }

        // this response contains additional data from customization that is not stored in core banking and gets associated via "finnovaId" aka "kontoart"
        const settingsApiProducts: IProduct[] = success.payload.products;

        const nonCardProducts = settingsApiProducts.filter((p) => p.category !== DefaultProductCategory.CARDS);

        const request: IFetchFinnovaRequest = {
            languageCode: i18n.language,
            vtlnr: selectedCustomerBase.vtlnr,
            kdlnr: selectedCustomerBase.externalId,
        };
        const kontodepots: IKontodepot[] = yield call([api, api.getKontodepots], request);

        let augmentedKontoDepots = kontodepots;

        try {
            const accounts: IAccount[] = yield call([api, api.getAccounts], request);

            augmentedKontoDepots = kontodepots.map((depot) => {
                const depotAccount = accounts.find((account) => `${account.accountKey}` === `${depot.ktLnr}`);

                return depotAccount
                    ? {
                          ...depot,
                          accountCategory: depotAccount.accountCategory,
                          accountCategoryId: depotAccount.accountCategoryId ? `${depotAccount.accountCategoryId}` : undefined,
                      }
                    : depot;
            });
        } catch (error: unknown) {
            yield put(logError(`could not fetch accounts, so could not augment ist products: ${error}`));
        }

        const istProducts = augmentedKontoDepots.reduce<IIstProduct[]>((acc, kontodepot) => {
            const productSettings = nonCardProducts.find((p) => p.finnovaId === `${kontodepot.kontoart}`);

            return productSettings
                ? [
                      ...acc,
                      {
                          ...kontodepot,
                          ...productSettings,
                      },
                  ]
                : acc;
        }, []);

        yield put(fetchIstProductsSuccess(istProducts));
    } catch (error: unknown) {
        yield put(logError(`could not fetch ist products: ${error}`));
        yield put(fetchIstProductsFailure());
    }
}

// eslint-disable-next-line consistent-return
function* handleFetchSollProducts({ payload: categories }: Action<string[]>) {
    const api: FinnovaApiService = yield getContext('finnovaApiService');

    try {
        yield put(fetchProducts(categories));
        const { success } = yield race({
            success: take(fetchProductsActionTaker(fetchProductsSuccess.type, categories)),
            failure: take(fetchProductsActionTaker(fetchProductsFailure.type, categories)),
        });
        if (!success) {
            throw new Error('Could not fetch products from customization');
        }

        const isStrongAuthenticated: boolean = yield select(applicationSelectors.isStrongAuthenticated);
        const products: IProduct[] = success.payload.products;
        if (!isStrongAuthenticated) {
            yield put(fetchSollProductsSuccess(products));
        }
        let selectedCustomerBase: ICustomerBase | undefined = yield select(applicationSelectors.getSelectedCustomerBaseForProducts);
        if (!selectedCustomerBase) {
            const { payload } = yield take(selectCustomerBaseForProducts);
            selectedCustomerBase = payload;
        }
        if (!selectedCustomerBase) {
            throw new Error('Cant fetch soll products without customer base');
        }

        const request: IFetchFinnovaRequest = {
            languageCode: i18n.language,
            vtlnr: selectedCustomerBase.vtlnr,
            kdlnr: selectedCustomerBase.externalId,
        };
        const kundenToepfe: ICrmKundenTopf[] = yield call([api, api.getCrmKundenToepfe], request);
        const sollProductsAvailableInCustomization: IProduct[] = yield getProductsAvailableInCustomization(kundenToepfe, products, [2]);

        yield put(fetchSollProductsSuccess(sollProductsAvailableInCustomization));
    } catch (error: unknown) {
        yield put(logError(`could not fetch soll products: ${error}`));
        yield put(fetchSollProductsFailure(error));
    }
}

function* handleFetchCardProducts() {
    const api: FinnovaApiService = yield getContext('finnovaApiService');

    const currentCardProducts: IProduct[] | undefined = yield select(finnovaApiSelectors.getCardProducts);
    if (currentCardProducts) {
        return;
    }

    const cardCategories = [DefaultProductCategory.CARDS];

    try {
        yield put(fetchProducts(cardCategories));
        const { success } = yield race({
            success: take(fetchProductsActionTaker(fetchProductsSuccess.type, cardCategories)),
            failure: take(fetchProductsActionTaker(fetchProductsFailure.type, cardCategories)),
        });
        if (!success) {
            throw new Error('Could not fetch card products from customization');
        }

        const isStrongAuthenticated: boolean = yield select(applicationSelectors.isStrongAuthenticated);

        const fetchedCardProducts: IProduct[] = success.payload.products;

        let availableCardProducts: IProduct[];

        if (!isStrongAuthenticated) {
            availableCardProducts = fetchedCardProducts;
        } else {
            let selectedCustomerBase: ICustomerBase | undefined = yield select(applicationSelectors.getSelectedCustomerBaseForProducts);

            if (!selectedCustomerBase) {
                const { payload } = yield take(selectCustomerBaseForProducts);
                selectedCustomerBase = payload;
            }
            if (!selectedCustomerBase) {
                throw new Error("Can't fetch kann card products without customer base");
            }

            const request: IFetchFinnovaRequest = {
                languageCode: i18n.language,
                vtlnr: selectedCustomerBase.vtlnr,
                kdlnr: selectedCustomerBase.externalId,
            };
            const kundenToepfe: ICrmKundenTopf[] = yield call([api, api.getCrmKundenToepfe], request);

            // card products available in customization
            availableCardProducts = yield getProductsAvailableInCustomization(kundenToepfe, fetchedCardProducts, [0, 2]);
        }

        const kannCardProductsWithSuitablePermissions: IProduct[] = availableCardProducts.filter((product) =>
            product.permissions.includes(isStrongAuthenticated ? ProductPermission.STRONG : ProductPermission.WEAK),
        );

        yield put(fetchCardProductsSuccess(kannCardProductsWithSuitablePermissions));
    } catch (error: unknown) {
        yield put(logError(`could not fetch card products: ${error}`));
        yield put(fetchCardProductsFailure());
    }
}

function* handleFetchPowerOfAttorney({ payload: customerBase }: Action<ICustomerBase | undefined>) {
    const api: FinnovaApiService = yield getContext('finnovaApiService');
    try {
        yield put(showProgressIndicator());
        const selectedCustomerBaseForEServices: ICustomerBase | undefined = yield select(applicationSelectors.getSelectedCustomerBaseForEServices);

        const resolvedCustomerBase = customerBase || selectedCustomerBaseForEServices;

        if (!resolvedCustomerBase) {
            throw new Error('No customer base provided');
        }

        const { vtlnr, externalId: kdlnr }: ICustomerBase = resolvedCustomerBase;
        const request: IFetchFinnovaRequest = {
            languageCode: i18n.language,
            vtlnr,
            kdlnr,
        };
        const powerOfAttorneys: IPowerOfAttorney[] = yield call([api, api.getVollmachten], request);
        yield put(fetchPowerOfAttorneySuccess(powerOfAttorneys));
    } catch (error: unknown) {
        yield put(logError(`could not fetch power of attorney: ${error}`));
        yield put(fetchPowerOfAttorneyFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleFetchIstCards() {
    const api: FinnovaApiService = yield getContext('finnovaApiService');

    try {
        const isStrongAuthenticated: boolean = yield select(applicationSelectors.isStrongAuthenticated);
        if (!isStrongAuthenticated) {
            throw new Error('Cannot fetch IST cards for weak or anonymous users');
        }
        let selectedCustomerBase: ICustomerBase | undefined = yield select(applicationSelectors.getSelectedCustomerBaseForProducts);
        if (!selectedCustomerBase) {
            const { payload } = yield take(selectCustomerBaseForProducts);
            selectedCustomerBase = payload;
        }
        if (!selectedCustomerBase) {
            throw new Error('Cant fetch ist cards without customer base');
        }

        const cardsCategory = [DefaultProductCategory.CARDS];
        yield put(fetchProducts(cardsCategory));
        const { success } = yield race({
            success: take(fetchProductsActionTaker(fetchProductsSuccess.type, cardsCategory)),
            failure: take(fetchProductsActionTaker(fetchProductsFailure.type, cardsCategory)),
        });
        if (!success) {
            throw new Error('Could not fetch cards from customization');
        }
        const cardProducts: IProduct[] = success.payload.products;

        const request: IFetchFinnovaRequest = {
            languageCode: i18n.language,
            vtlnr: selectedCustomerBase.vtlnr,
            kdlnr: selectedCustomerBase.externalId,
        };

        const tenant: Tenant = yield select(getTenant);
        const allCards: IKarte[] = yield call([api, api.getKarten], request);
        const allIstCards: IIstKarte[] = [];

        for (const card of allCards) {
            const cardProduct = cardProducts.find((product) => product.finnovaId === `${card.kartenart}`);
            if (cardProduct) {
                const zeileAFetchMethod = isCreditCard(tenant, cardProduct) ? api.getKreditkarte : api.getDebitkarte;
                const { zeileA } = yield call([api, zeileAFetchMethod], { cardLnr: card.cardLnr, kdlnr: card.kdLnr });
                allIstCards.push({ ...card, ...cardProduct, zeileA: pascalCaseWords(zeileA) });
            }
        }

        yield put(fetchIstCardsSuccess(allIstCards));
    } catch (error: unknown) {
        yield put(logError(`could not fetch ist cards: ${error}`));
        yield put(fetchIstCardsFailure());
    }
}

function* handlefailure(action: ReturnType<typeof fetchSollProductsFailure>) {
    yield put(
        addNotification({
            title: i18n.t('finnova-api.fetch-products.failure.title'),
            text: i18n.t('finnova-api.fetch-products.failure.body'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleFetchPowerOfAttorneyFailure() {
    yield put(
        addNotification({
            title: i18n.t('finnova-api.fetch-power-of-attorny.failure.title'),
            text: i18n.t('finnova-api.fetch-power-of-attorny.failure.body'),
            type: NotificationType.ERROR,
        }),
    );
}

function isOwnerOfContract(currentContractState: IEBankingContract, eBankingVT: IEbankingVT): boolean {
    if (currentContractState.isOwner === undefined) {
        return Boolean(eBankingVT.eigenerZugriff);
    }

    if (!currentContractState.isOwner) {
        return false;
    }

    return Boolean(eBankingVT.eigenerZugriff);
}

function* handleFetchEbankingContracts() {
    const api: FinnovaApiService = yield getContext('finnovaApiService');
    const allProductsExceptCards: DefaultProductCategory[] = [
        DefaultProductCategory.PAYMENT,
        DefaultProductCategory.SAVING,
        DefaultProductCategory.PROVISION,
        DefaultProductCategory.INVESTMENT,
        DefaultProductCategory.FINANCING,
    ];

    try {
        yield put(showProgressIndicator());
        yield put(fetchProducts(allProductsExceptCards));
        const { success } = yield race({
            success: take(fetchProductsActionTaker(fetchProductsSuccess.type, allProductsExceptCards)),
            failure: take(fetchProductsActionTaker(fetchProductsFailure.type, allProductsExceptCards)),
        });
        if (!success) {
            throw new Error('Could not fetch products from customization');
        }
        const products: IProduct[] = success.payload.products;
        let selectedCustomerBase: ICustomerBase | undefined = yield select(applicationSelectors.getSelectedCustomerBaseForEServices);
        if (!selectedCustomerBase) {
            const { payload } = yield take(selectCustomerBaseForEServices);
            selectedCustomerBase = payload;
        }
        if (!selectedCustomerBase) {
            throw new Error('Cant fetch ist ebanking contracts customer base');
        }
        const { vtlnr, externalId: kdlnr, displayName } = selectedCustomerBase;
        const request: IFetchFinnovaRequest = {
            languageCode: i18n.language,
            vtlnr,
            kdlnr,
        };
        const eBankingVTS: IEbankingVT[] = yield call([api, api.getEbankingVT], request);
        const translationSelector = getTranslationSelector(i18n.language);

        // regarding finnova spec: status of eBankingVTS actually defines the status of the whole contract that's why
        // the first occurred VTS status can be taken as the valid one for the whole contract. All the VTS will have the same status.
        const eBankingContractMap: { [key: string]: IEBankingContract } = eBankingVTS.reduce((acc, val) => {
            const contract: IEBankingContract = acc[val.vtId] || {
                owner: displayName,
                contractId: val.vtId,
                contractLnr: val.vtLnr,
                authorizedPerson: val.kundenbezeichnung,
                accounts: [],
                status: val.status,
            };
            contract.isOwner = isOwnerOfContract(contract, val);
            const product = products.find(({ finnovaId }) => finnovaId === `${val.kontoart}`);
            const accountTitle = (product && translationSelector(product.title)) || '';
            if (val.status === VertragsStatus.GUELTIG || val.status === VertragsStatus.GESPERRT) {
                contract.accounts.push({
                    accountTitle,
                    accountNr: `${val.kontonummer}` || '-',
                    IBAN: val.iban || '-',
                    status: val.status,
                });
            }

            return {
                ...acc,
                [val.vtId]: contract,
            };
        }, {});
        const eBankingContracts: IEBankingContract[] = Object.keys(eBankingContractMap).map((key) => eBankingContractMap[key]);
        yield put(fetchEBankingContractsSuccess(eBankingContracts));
    } catch (error: unknown) {
        yield put(logError(`could not fetch ebanking contracts: ${error}`));
        yield put(fetchEBankingContractsFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleFetchMasterDataFailure() {
    yield put(
        addNotification({
            title: i18n.t('finnova-api.fetch-master-data.failure.title'),
            text: i18n.t('finnova-api.fetch-master-data.failure.body'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleFetchCommunicationEntries() {
    const api: FinnovaApiService = yield getContext('finnovaApiService');
    try {
        let selectedCustomerBase: ICustomerBase | undefined = yield select(applicationSelectors.getSelectedCustomerBaseForEServices);
        if (!selectedCustomerBase) {
            const { payload } = yield take(selectCustomerBaseForEServices);
            selectedCustomerBase = payload;
        }
        if (!selectedCustomerBase) {
            throw new Error('Cant fetch communication entries without customer base');
        }
        const { vtlnr, externalId: kdlnr } = selectedCustomerBase;
        const request: IFetchFinnovaRequest = {
            languageCode: i18n.language,
            vtlnr,
            kdlnr,
        };
        const communicationEntries: ICommunicationEntries = yield call([api, api.getCommunicationEntries], request);
        yield put(fetchCommunicationEntriesSuccess(communicationEntries));
    } catch (error: unknown) {
        yield put(logError(`could not fetch communication entries: ${error}`));
        yield put(fetchCommunicationEntriesFailure());
    }
}

function* handleFetchKontodepots() {
    const api: FinnovaApiService = yield getContext('finnovaApiService');
    try {
        let selectedCustomerBase: ICustomerBase | undefined = yield select(applicationSelectors.getSelectedCustomerBaseForEServices);
        if (!selectedCustomerBase) {
            const { payload } = yield take(selectCustomerBaseForEServices);
            selectedCustomerBase = payload;
        }
        if (!selectedCustomerBase) {
            throw new Error('Cant fetch kontodepots without customer base');
        }
        const { vtlnr, externalId: kdlnr } = selectedCustomerBase;
        const request: IFetchFinnovaRequest = {
            languageCode: i18n.language,
            vtlnr,
            kdlnr,
        };
        const kontodepots: IKontodepot[] = yield call([api, api.getKontodepots], request);
        yield put(fetchKontodepotsSuccess(kontodepots));
    } catch (error: unknown) {
        yield put(logError(`could not fetch kontodepots: ${error}`));
        yield put(fetchKontodepotsFailure());
    }
}

function* handleFetchAddresses() {
    const api: FinnovaApiService = yield getContext('finnovaApiService');
    try {
        let selectedCustomerBase: ICustomerBase | undefined = yield select(applicationSelectors.getSelectedCustomerBaseForEServices);
        if (!selectedCustomerBase) {
            const { payload } = yield take(selectCustomerBaseForEServices);
            selectedCustomerBase = payload;
        }
        if (!selectedCustomerBase) {
            throw new Error('Cant fetch addresses contracts customer base');
        }
        const { vtlnr, externalId: kdlnr } = selectedCustomerBase;
        const request: IFetchFinnovaRequest = {
            languageCode: i18n.language,
            vtlnr,
            kdlnr,
        };
        const addresses: IAddress[] = yield call([api, api.getAddresses], request);
        yield put(fetchAddressesSuccess(addresses));
    } catch (error: unknown) {
        yield put(logError(`could not fetch addresses: ${error}`));
        yield put(fetchAddressesFailure());
    }
}

function* handleFetchCodes({ payload: columnName }: Action<FinnovaCodeColumn>) {
    const api: FinnovaApiService = yield getContext('finnovaApiService');
    try {
        const codeTable: FinnovaCodeTable = yield select(finnovaApiSelectors.getFinnovaCodeTable);
        let codes: IFinnovaCode[] | undefined = codeTable[columnName];
        if (!codes) {
            const { vtlnr, externalId: kdlnr }: ICustomerBase = yield select(customerBaseApiSelectors.getOwnCustomerBase);
            const request: IFetchFinnovaCodesRequest = {
                languageCode: i18n.language,
                vtlnr,
                kdlnr,
                columnName,
            };
            codes = yield call([api, api.getCodes], request);
        }
        if (!codes) {
            throw new Error(`Could not fetch codes for column ${columnName}`);
        }
        yield put(fetchCodesSuccess({ codes, columnName }));
    } catch (error: unknown) {
        yield put(logError(`could not fetch codes: ${error}`));
        yield put(fetchCodesFailure());
    }
}

export function* finnovaApiSaga() {
    yield all([
        call(addTranslations),
        takeEvery(fetchCodes, handleFetchCodes),
        takeLatest(fetchKontodepots, handleFetchKontodepots),
        takeLatest(fetchKannProducts, handleFetchKannProducts),
        takeLatest(fetchSollProducts, handleFetchSollProducts),
        takeLatest(fetchIstProducts, handleFetchIstProducts),
        takeLatest(fetchCardProducts, handleFetchCardProducts),
        takeLatest(fetchIstCards, handleFetchIstCards),
        takeLatest(fetchPowerOfAttorney, handleFetchPowerOfAttorney),
        takeLatest([fetchProductsFailure, fetchSollProductsFailure, fetchIstProductsFailure, fetchCardProductsFailure], handlefailure),
        takeLatest(fetchPowerOfAttorneyFailure, handleFetchPowerOfAttorneyFailure),
        takeLatest(fetchEBankingContracts, handleFetchEbankingContracts),
        takeLatest(fetchCommunicationEntries, handleFetchCommunicationEntries),
        takeLatest(fetchAddresses, handleFetchAddresses),
        debounce(1000, [fetchAddressesFailure, fetchCommunicationEntriesFailure], handleFetchMasterDataFailure),
    ]);
}
